import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Clock, History, LogOut, User, Download } from 'lucide-react';
import ConfirmationModal from '../../../components/common/Modal/ConfirmationModal';
import { usePWA } from '../../../context/PWAContext';

const CustomerSidebar = ({ 
    activeView, 
    token, 
    customerData, 
    supplierData, 
    onTabChange 
}) => {
    const navigate = useNavigate();
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const { promptInstall, openInstallModal, isInstalled } = usePWA();

    const handleNavigation = (view) => {
        const activeToken = token && token !== 'null' && token !== 'undefined' ? token : (localStorage.getItem('customer_token') || sessionStorage.getItem('customer_token') || '');

        if (view === 'shop') {
            if (window.location.pathname === '/shop') {
                onTabChange && onTabChange('All');
            } else {
                navigate(`/shop?token=${activeToken}&tab=All`);
            }
        } else if (view === 'pending') {
            if (window.location.pathname === '/shop') {
                onTabChange && onTabChange('Pending Orders');
            } else {
                navigate(`/shop?token=${activeToken}&tab=Pending`);
            }
        } else if (view === 'history') {
            navigate(`/status/orders?token=${activeToken}`);
        } else if (view === 'profile') {
            navigate(`/customer/profile?token=${activeToken}`);
        }
    };

    const handleLogout = () => {
        setIsLogoutModalOpen(true);
    };

    const confirmLogout = () => {
        localStorage.removeItem('customer_token');
        sessionStorage.removeItem('customer_token');
        navigate(`/order/login${supplierData?.supplierId ? `/${supplierData.supplierId}` : ''}`);
    };

    const menuItems = [
        { id: 'shop', label: 'Shop Products', icon: ShoppingBag },
        { id: 'pending', label: 'Pending Orders', icon: Clock },
        { id: 'history', label: 'Order History (All)', icon: History },
        { id: 'profile', label: 'My Profile', icon: User }
    ];

    return (
        <>
        <aside className="hidden lg:flex w-64 bg-white border-r border-slate-100 flex-col h-screen sticky top-0 z-40">
            {/* Branding Section */}
            <div className="p-6 border-b border-slate-50 bg-gradient-to-br from-slate-50 to-cyan-50/20">
                <div className="flex items-center gap-3 mb-4">
                    <img src="/logo.jpg" alt="Logo" className="w-8 h-8 rounded-lg object-cover shadow-sm" />
                    <span className="font-black text-slate-800 tracking-tight text-lg">Yaseero</span>
                </div>
                <div className="pt-3 border-t border-slate-100/80">
                    <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest mb-1">Supplier</p>
                    <p className="text-sm font-extrabold text-slate-700 truncate">{supplierData?.businessName || 'Supplier'}</p>
                </div>
            </div>

            {/* Profile Section */}
            <div className="px-6 py-4 border-b border-slate-50 flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-50 text-cyan-600 rounded-xl flex items-center justify-center font-bold border border-cyan-100">
                    {customerData?.customerName?.charAt(0) || customerData?.name?.charAt(0) || 'C'}
                </div>
                <div className="min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-800 truncate">{customerData?.customerName || customerData?.name || 'Customer'}</h4>
                    <p className="text-xs text-slate-400 truncate">{customerData?.contactPerson || 'B2B Client'}</p>
                </div>
            </div>

            {/* Menu Items */}
            <nav className="flex-1 px-4 py-6 space-y-1.5">
                {menuItems.map(item => {
                    const Icon = item.icon;
                    const isActive = activeView === item.id;

                    return (
                        <button
                            key={item.id}
                            onClick={() => handleNavigation(item.id)}
                            className={`w-full flex items-center gap-3.5 px-4 py-3.5 rounded-2xl font-bold text-sm transition-all relative group ${
                                isActive 
                                    ? 'bg-cyan-50 text-cyan-600 shadow-sm border border-cyan-100/50' 
                                    : 'text-slate-600 hover:bg-slate-50 border border-transparent'
                            }`}
                        >
                            <Icon size={18} className={`transition-transform duration-200 group-hover:scale-105 ${isActive ? 'text-cyan-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
                            <span className="truncate">{item.label}</span>
                            {isActive && (
                                <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-cyan-500 shadow-sm shadow-cyan-300" />
                            )}
                        </button>
                    );
                })}
            </nav>

            {/* Footer Section */}
            <div className="p-4 border-t border-slate-50 bg-slate-50/50 space-y-2">
                <button
                    onClick={promptInstall}
                    className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold rounded-2xl text-xs transition-all group"
                >
                    <Download size={15} className="transition-transform group-hover:scale-110" />
                    <span>{isInstalled ? 'App Installed' : 'Install App'}</span>
                </button>
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2.5 px-4 py-3 bg-rose-50 hover:bg-rose-100 text-rose-600 font-bold rounded-2xl text-sm transition-all group"
                >
                    <LogOut size={16} className="transition-transform group-hover:-translate-x-0.5" />
                    Exit Customer Portal
                </button>
            </div>

        </aside>
        <ConfirmationModal
            isOpen={isLogoutModalOpen}
            onClose={() => setIsLogoutModalOpen(false)}
            onConfirm={confirmLogout}
            title="Exit Customer Portal?"
            message="Are you sure you want to exit the customer portal and sign out? You will need your PIN to sign back in."
            confirmText="Yes, Exit"
            cancelText="No, Keep Logged In"
            type="danger"
            icon={<LogOut className="w-6 h-6 text-red-600" />}
        />
        </>
    );
};

export default CustomerSidebar;
